import { useState, useMemo, useEffect, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  flexRender,
  type ColumnDef,
  type SortingState,
} from '@tanstack/react-table'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Search,
  Plus,
  MoreVertical,
  Eye,
  Ban,
  ChevronLeft,
  ChevronRight,
  Users,
  RefreshCw,
  Loader2,
  AlertCircle,
  Gift,
  Phone,
  Wallet,
  Calendar,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useAuth } from '@/components/auth/auth-provider'
import { getUserService } from '@/domain/user'
import { BankLogo } from '@/components/ui/bank-logo'
import type { Member } from '@/domain/user/user.types'
import { MemberDetailDialog } from './member-detail-dialog'
import { AddMemberDialog } from './add-member-dialog'
import { useCurrency } from '@/stores/currency-store'

export function MembersPage() {
  const { t } = useTranslation()
  const { sessionToken } = useAuth()
  const { format: formatCurrency, currency } = useCurrency()
  const [searchParams, setSearchParams] = useSearchParams()
  
  // State
  const [sorting, setSorting] = useState<SortingState>([])
  const [globalFilter, setGlobalFilter] = useState('')
  const [members, setMembers] = useState<Member[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [refreshKey, setRefreshKey] = useState(0) // 🆕 สำหรับ trigger refresh
  const [total, setTotal] = useState(0)
  const [searchInput, setSearchInput] = useState('')
  
  // Pagination
  const [limit, setLimit] = useState(20)
  
  // Dialog state
  const [selectedMember, setSelectedMember] = useState<Member | null>(null)
  const [isDetailOpen, setIsDetailOpen] = useState(false)
  const [isAddOpen, setIsAddOpen] = useState(false)

  // Open member detail
  const handleViewMember = (member: Member) => {
    setSelectedMember(member)
    setIsDetailOpen(true)
  }

  // ============================================
  // Fetch Members from API
  // ============================================
  
  const fetchMembers = useCallback(async (searchTerm: string = '') => {
    if (!sessionToken) {
      setError(t('members.pleaseLogin'))
      setIsLoading(false)
      return
    }
    
    setIsLoading(true)
    setError(null)
    
    try {
      console.log('📡 Fetching members...', { page, limit, search: searchTerm })
      
      const service = await getUserService()
      const result = await service.getMembers(sessionToken, {
        page,
        limit,
        search: searchTerm,
        orderBy: 'desc',
        orderByField: 'createdAt',
      })
      
      console.log('✅ Members fetched:', result)
      
      setMembers(result.items)
      setTotalPages(result.totalPages)
      setTotal(result.total)
      
    } catch (err: any) {
      console.error('❌ Error fetching members:', err)
      setError(err.message || t('members.loadError'))
    } finally {
      setIsLoading(false)
    }
  }, [sessionToken, page, limit])
  
  // ✅ ตรวจสอบ memberId จาก URL (สำหรับ notification click)
  useEffect(() => {
    const memberId = searchParams.get('memberId')
    if (memberId && sessionToken) {
      // ดึงข้อมูล member และเปิด dialog
      const loadMember = async () => {
        try {
          const service = await getUserService()
          const member = await service.getMember(sessionToken, memberId)
          if (member) {
            setSelectedMember(member)
            setIsDetailOpen(true)
            // ลบ memberId จาก URL หลังจากเปิด dialog
            searchParams.delete('memberId')
            setSearchParams(searchParams, { replace: true })
          }
        } catch (error) {
          console.error('Error loading member from URL:', error)
        }
      }
      loadMember()
    }
  }, [searchParams, sessionToken, setSearchParams])

  // Initial load & pagination & refresh
  useEffect(() => {
    fetchMembers(globalFilter)
  }, [page, limit, refreshKey]) // 🆕 เพิ่ม refreshKey
  
  // Search with debounce
  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== globalFilter) {
        setGlobalFilter(searchInput)
        setPage(1) // Reset to first page on search
        fetchMembers(searchInput)
      }
    }, 500)
    
    return () => clearTimeout(timer)
  }, [searchInput])
  
  // Refresh handler
  const handleRefresh = () => {
    console.log('🔄 [MembersPage] Manual refresh triggered')
    setRefreshKey(prev => prev + 1)
  }
  
  // Handle dialog saved - reload list after action
  const handleMemberSaved = () => {
    console.log('🔄 [MembersPage] Member saved, triggering refresh...')
    setRefreshKey(prev => prev + 1) // 🆕 trigger useEffect เพื่อ refresh
  }

  // Handle add member success
  const handleAddSuccess = () => {
    console.log('🔄 [MembersPage] Member added, triggering refresh...')
    setRefreshKey(prev => prev + 1)
  }

  // ============================================
  // Table Columns - จัดเรียงตามความสำคัญ
  // ============================================
  
  const columns = useMemo<ColumnDef<Member>[]>(() => [
    // 1. ยูสเซอร์ + ชื่อ (รวมกัน - ประหยัดพื้นที่)
    {
      id: 'user',
      header: t('members.columns.customer'),
      cell: ({ row }) => (
        <div className="flex items-center gap-3">
          <div className="relative">
            <div className="h-10 w-10 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
              <span className="font-bold text-primary">
                {(row.original.firstName?.[0] || 'U').toUpperCase()}
              </span>
            </div>
            {/* Status indicator dot */}
            <div className={cn(
              "absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background",
              row.original.status === 'Active' ? 'bg-emerald-500' : 
              row.original.status === 'Suspend' ? 'bg-red-500' : 
              row.original.status === 'Watchout' ? 'bg-amber-500' : 'bg-gray-400'
            )} />
          </div>
          <div className="min-w-0">
            <div className="font-medium truncate">{row.original.firstName} {row.original.lastName}</div>
            <div className="text-xs text-muted-foreground flex items-center gap-1">
              <span className="font-mono">{row.original.agentUsername || row.original.phone}</span>
            </div>
          </div>
        </div>
      ),
    },
    // 2. เครดิต + สถานะโปร (สำคัญที่สุด!)
    {
      id: 'credit',
      header: t('members.columns.credit'),
      cell: ({ row }) => {
        const balance = row.original.balance || 0
        const hasPromo = row.original.promotion?.max_withdraw && row.original.promotion.max_withdraw > 0
        const sumDW = (row.original.sumDepositWithdraw || {}) as Record<string, number>
        const sumWL = (row.original.sumWinlose || {}) as Record<string, number>
        const turnover = (sumDW.sum_turn_over_cash || 0) + (sumDW.sum_turn_over_bonus || 0)
        const validBet = (sumWL.sum_vl_before_w || 0) * -1
        const turnoverRemaining = turnover - validBet
        
        return (
          <div className="text-right">
            <div className={cn(
              "text-lg font-bold",
              balance > 0 ? "text-emerald-600" : "text-muted-foreground"
            )}>
              {formatCurrency(balance)}
            </div>
            {hasPromo ? (
              <Badge variant="outline" className="mt-1 bg-orange-50 text-orange-700 border-orange-200 text-xs">
                {t('members.labels.promoWithTurnover')} {turnoverRemaining > 0 ? formatCurrency(turnoverRemaining) : '✓'}
              </Badge>
            ) : (
              <span className="text-xs text-emerald-600">{t('members.labels.canWithdraw')}</span>
            )}
          </div>
        )
      },
    },
    // 3. บัญชีธนาคาร
    {
      id: 'bank',
      header: t('members.columns.account'),
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <BankLogo bankValue={row.original.bankName} size="sm" />
          <div className="min-w-0">
            <div className="font-mono text-sm font-medium">{row.original.bankAccount || '-'}</div>
            <div className="text-xs text-muted-foreground truncate">{row.original.phone}</div>
          </div>
        </div>
      ),
    },
    // 4. ฝาก / ถอน / กำไร (3 in 1)
    {
      id: 'financeSummary',
      header: t('members.columns.financeSummary'),
      cell: ({ row }) => {
        const sumDW = (row.original.sumDepositWithdraw || {}) as Record<string, number>
        const deposit = sumDW.sum_deposit_cash_all || 0
        const withdraw = sumDW.sum_withdraw_cash_all || 0
        const bonus = sumDW.sum_deposit_bonus_all || 0
        const netProfit = deposit - withdraw - bonus
        
        return (
          <div className="text-xs space-y-0.5">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('members.labels.deposit')}</span>
              <span className="font-medium text-emerald-600">+{formatCurrency(deposit)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('members.labels.withdraw')}</span>
              <span className="font-medium text-red-600">-{formatCurrency(withdraw)}</span>
            </div>
            <div className="flex justify-between border-t pt-0.5">
              <span className="text-muted-foreground">{t('members.labels.profit')}</span>
              <span className={cn("font-bold", netProfit >= 0 ? "text-emerald-600" : "text-red-600")}>
                {formatCurrency(netProfit)}
              </span>
            </div>
          </div>
        )
      },
    },
    // 5. แพ้/ชนะ
    {
      id: 'winlose',
      header: t('members.columns.winlose'),
      cell: ({ row }) => {
        const sumWL = (row.original.sumWinlose || {}) as Record<string, number>
        const winlose = sumWL.sum_wl_all || 0
        
        return (
          <span className={cn(
            "px-2 py-1 rounded-full text-sm font-medium",
            winlose >= 0 
              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-200" 
              : "bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-200"
          )}>
            {formatCurrency(winlose)}
          </span>
        )
      },
    },
    // 6. วันที่สมัคร
    {
      id: 'createdAt',
      header: t('members.columns.createdAt'),
      cell: ({ row }) => {
        const createdAt = row.original.createdAt
        if (!createdAt) return <span className="text-muted-foreground">-</span>
        
        const date = new Date(createdAt)
        const now = new Date()
        const diffTime = Math.abs(now.getTime() - date.getTime())
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
        
        // Format: "25 ธ.ค. 67"
        const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
        const day = date.getDate()
        const month = t(`members.dateMonths.${monthKeys[date.getMonth()]}`)
        const year = (date.getFullYear() + 543) % 100 // ปี พ.ศ. 2 หลัก
        const formattedDate = `${day} ${month} ${year}`
        
        // Time format: "14:30"
        const time = date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })
        
        // สีตามความใหม่
        let badgeClass = 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400'
        let label = ''
        
        if (diffDays <= 1) {
          badgeClass = 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
          label = t('members.labels.new')
        } else if (diffDays <= 7) {
          badgeClass = 'bg-blue-100 text-blue-700 dark:bg-blue-900/50 dark:text-blue-300'
          label = `${diffDays} ${t('members.labels.days')}`
        } else if (diffDays <= 30) {
          badgeClass = 'bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-300'
          label = `${Math.floor(diffDays / 7)} ${t('members.labels.weeks')}`
        }
        
        return (
          <div className="text-xs space-y-1">
            <div className="flex items-center gap-1.5">
              <Calendar className="h-3 w-3 text-muted-foreground" />
              <span className="font-medium">{formattedDate}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <span className="text-muted-foreground ml-[18px]">{time}</span>
              {label && (
                <span className={cn("px-1.5 py-0.5 rounded text-[10px] font-medium", badgeClass)}>
                  {label}
                </span>
              )}
            </div>
          </div>
        )
      },
    },
    // 7. สถานะ
    {
      accessorKey: 'status',
      header: t('members.columns.status'),
      cell: ({ row }) => {
        const status = row.original.status
        const statusConfig: Record<string, { labelKey: string; variant: 'default' | 'secondary' | 'destructive' | 'outline' }> = {
          'Active': { labelKey: 'members.status.normal', variant: 'default' },
          'Suspend': { labelKey: 'members.status.suspend', variant: 'destructive' },
          'Watchout': { labelKey: 'members.status.watchout', variant: 'outline' },
          'Inactive': { labelKey: 'members.status.noActivity', variant: 'secondary' },
          'Pending': { labelKey: 'members.status.pending', variant: 'secondary' },
          'Rejected': { labelKey: 'members.status.rejected', variant: 'destructive' },
          'Cancel': { labelKey: 'members.status.cancel', variant: 'secondary' },
          'Banned': { labelKey: 'members.status.banned', variant: 'destructive' },
        }
        const config = statusConfig[status] || { labelKey: '', variant: 'secondary' as const }
        return (
          <Badge variant={config.variant} className={cn(
            config.variant === 'outline' && 'border-amber-500 text-amber-600'
          )}>
            {config.labelKey ? t(config.labelKey) : (status || '-')}
          </Badge>
        )
      },
    },
    // 8. Actions
    {
      id: 'actions',
      header: '',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={(e) => e.stopPropagation()}>
              <MoreVertical className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48">
            <DropdownMenuItem className="gap-2 cursor-pointer" onClick={() => handleViewMember(row.original)}>
              <Eye className="h-4 w-4" />
              {t('members.actions.viewDetails')}
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2 cursor-pointer">
              <RefreshCw className="h-4 w-4" />
              {t('members.actions.updateData')}
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2 text-amber-600 cursor-pointer">
              <AlertCircle className="h-4 w-4" />
              {t('members.actions.resetPromo')}
            </DropdownMenuItem>
            <DropdownMenuItem className="gap-2 text-red-500 cursor-pointer">
              <Ban className="h-4 w-4" />
              {t('members.actions.banAccount')}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ], [t, handleViewMember])

  const table = useReactTable({
    data: members,
    columns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    manualPagination: true, // We handle pagination server-side
  })

  // ============================================
  // Render
  // ============================================

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Header - Responsive */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-xl md:text-2xl font-bold">{t('members.title')}</h1>
          <p className="text-sm text-muted-foreground">
            {t('members.subtitle')} • {total.toLocaleString()} {t('members.items')}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={handleRefresh} disabled={isLoading}>
            <RefreshCw className={cn("h-4 w-4", isLoading && "animate-spin")} />
          </Button>
          <Button className="gap-2" onClick={() => setIsAddOpen(true)}>
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">{t('members.addMember')}</span>
            <span className="sm:hidden">{t('common.add')}</span>
          </Button>
        </div>
      </div>

      {/* Search Bar - Responsive */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          placeholder={t('members.searchPlaceholder')}
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          className="h-10 w-full rounded-lg border bg-muted/50 pl-9 pr-4 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
        />
      </div>

      {/* Error State */}
      {error && (
        <div className="flex items-center gap-2 p-3 rounded-lg bg-destructive/10 text-destructive text-sm">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span className="flex-1">{error}</span>
          <Button variant="outline" size="sm" onClick={handleRefresh}>
            {t('members.tryAgain')}
          </Button>
        </div>
      )}
      
      {/* Loading State */}
      {isLoading && members.length === 0 ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          <span className="ml-2 text-muted-foreground">{t('members.loading')}</span>
        </div>
      ) : members.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-muted-foreground">
          <Users className="h-12 w-12 mb-2 opacity-50" />
          <span>{t('members.noData')}</span>
        </div>
      ) : (
        <>
          {/* Desktop Table - Hidden on Mobile */}
          <Card className="border-border/50 bg-card/50 backdrop-blur-sm hidden md:block">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50 border-b">
                    {table.getHeaderGroups().map((headerGroup) => (
                      <tr key={headerGroup.id}>
                        {headerGroup.headers.map((header) => (
                          <th
                            key={header.id}
                            className="px-4 py-3 text-left text-xs font-medium text-muted-foreground whitespace-nowrap"
                          >
                            {header.isPlaceholder
                              ? null
                              : flexRender(
                                  header.column.columnDef.header,
                                  header.getContext()
                                )}
                          </th>
                        ))}
                      </tr>
                    ))}
                  </thead>
                  <tbody>
                    {table.getRowModel().rows.map((row) => (
                      <tr
                        key={row.id}
                        className="border-t border-border/50 hover:bg-muted/30 transition-colors cursor-pointer"
                        onClick={() => handleViewMember(row.original)}
                      >
                        {row.getVisibleCells().map((cell) => (
                          <td key={cell.id} className="px-4 py-3 text-sm">
                            {flexRender(cell.column.columnDef.cell, cell.getContext())}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Mobile Card View - Only on Mobile */}
          <div className="space-y-3 md:hidden">
            {members.map((member) => {
              const balance = member.balance || 0
              const hasPromo = member.promotion?.max_withdraw && member.promotion.max_withdraw > 0
              const statusConfig: Record<string, { labelKey: string; color: string }> = {
                'Active': { labelKey: 'members.status.normal', color: 'bg-emerald-500' },
                'Suspend': { labelKey: 'members.status.suspend', color: 'bg-red-500' },
                'Watchout': { labelKey: 'members.status.watchout', color: 'bg-amber-500' },
              }
              const status = statusConfig[member.status] || { labelKey: '', color: 'bg-gray-400' }
              
              return (
                <Card 
                  key={member.id} 
                  className="cursor-pointer hover:shadow-md transition-shadow"
                  onClick={() => handleViewMember(member)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between mb-3">
                      {/* Avatar + Name */}
                      <div className="flex items-center gap-3">
                        <div className="relative">
                          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-primary/20 to-primary/10 flex items-center justify-center">
                            <span className="font-bold text-primary text-lg">
                              {(member.firstName?.[0] || 'U').toUpperCase()}
                            </span>
                          </div>
                          <div className={cn("absolute -bottom-0.5 -right-0.5 h-3 w-3 rounded-full border-2 border-background", status.color)} />
                        </div>
                        <div>
                          <div className="font-medium">{member.firstName} {member.lastName}</div>
                          <div className="text-xs text-muted-foreground flex items-center gap-1">
                            <Phone className="w-3 h-3" />
                            {member.phone}
                          </div>
                        </div>
                      </div>
                      
                      {/* Credit */}
                      <div className="text-right">
                        <div className={cn(
                          "text-lg font-bold",
                          balance > 0 ? "text-emerald-600" : "text-muted-foreground"
                        )}>
                          {formatCurrency(balance)}
                        </div>
                        <div className="text-xs text-muted-foreground flex items-center justify-end gap-1">
                          <Wallet className="w-3 h-3" />
                          {currency.symbol}
                        </div>
                      </div>
                    </div>
                    
                    {/* Tags */}
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-xs">
                        {status.labelKey ? t(status.labelKey) : member.status}
                      </Badge>
                      {hasPromo && (
                        <Badge variant="outline" className="bg-orange-50 text-orange-700 border-orange-200 text-xs">
                          <Gift className="w-3 h-3 mr-1" />
                          {t('members.labels.promoActive')}
                        </Badge>
                      )}
                      {member.bankAccount && (
                        <Badge variant="secondary" className="text-xs font-mono">
                          {member.bankAccount}
                        </Badge>
                      )}
                      {/* วันที่สมัคร */}
                      {member.createdAt && (
                        <Badge variant="outline" className="text-xs text-muted-foreground">
                          <Calendar className="w-3 h-3 mr-1" />
                          {(() => {
                            const date = new Date(member.createdAt)
                            const monthKeys = ['jan', 'feb', 'mar', 'apr', 'may', 'jun', 'jul', 'aug', 'sep', 'oct', 'nov', 'dec']
                            const day = date.getDate()
                            const month = t(`members.dateMonths.${monthKeys[date.getMonth()]}`)
                            const year = (date.getFullYear() + 543) % 100
                            return `${day} ${month} ${year}`
                          })()}
                        </Badge>
                      )}
                    </div>
                  </CardContent>
                </Card>
              )
            })}
          </div>

          {/* Pagination - Enhanced */}
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-4 p-4 bg-muted/30 rounded-lg border">
            <div className="flex items-center gap-4">
              <div className="text-sm text-muted-foreground">
                {t('members.showing')} {((page - 1) * limit) + 1} - {Math.min(page * limit, total)} {t('members.of')} {total.toLocaleString()} {t('members.items')}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-sm text-muted-foreground">{t('members.showing')}</span>
                <Select
                  value={String(limit)}
                  onValueChange={(v) => { setLimit(Number(v)); setPage(1) }}
                >
                  <SelectTrigger className="w-[70px] h-8">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="10">10</SelectItem>
                    <SelectItem value="20">20</SelectItem>
                    <SelectItem value="50">50</SelectItem>
                    <SelectItem value="100">100</SelectItem>
                  </SelectContent>
                </Select>
                <span className="text-sm text-muted-foreground">{t('members.items')}</span>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(1)}
                disabled={page === 1 || isLoading}
              >
                {t('members.page')} {t('members.first')}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={page <= 1 || isLoading}
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <span className="text-sm px-2 min-w-[100px] text-center">
                {t('members.page')} {page} / {totalPages || 1}
              </span>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages || isLoading}
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setPage(totalPages)}
                disabled={page >= totalPages || isLoading}
              >
                {t('members.page')} {t('members.last')}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Member Detail Dialog */}
      <MemberDetailDialog
        member={selectedMember}
        open={isDetailOpen}
        onOpenChange={setIsDetailOpen}
        onSaved={handleMemberSaved}
      />

      {/* Add Member Dialog */}
      <AddMemberDialog
        open={isAddOpen}
        onOpenChange={setIsAddOpen}
        onSuccess={handleAddSuccess}
      />
    </div>
  )
}
